import { useMemo } from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Sector,
} from "recharts";
import { BaseChartProps, ChartWrapper } from "./chart-utils";

import {
  chartColors,
  defaultResponsiveConfig,
  generateRandomData,
} from "./chart-helpers";

import {
  VerticalAlignmentType,
  HorizontalAlignmentType,
} from "recharts/types/component/DefaultLegendContent";

export interface PieChartProps extends BaseChartProps {
  innerRadius?: number;
  outerRadius?: number | string;
  activeIndex?: number;
  startAngle?: number;
  endAngle?: number;
  paddingAngle?: number;
  dataLabels?: boolean;
  labelLine?: boolean;
}

interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  payload: { name: string; value: number };
  percent: number;
  value: number;
  midAngle?: number;
  index?: number;
}

const renderActiveShape = (props: ActiveShapeProps) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    percent,
    value,
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 8}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
      <text
        x={cx}
        y={cy - 8}
        dy={8}
        textAnchor="middle"
        fill={chartColors.foreground}
        fontSize={14}
        fontWeight="bold"
      >
        {payload.name}
      </text>
      <text
        x={cx}
        y={cy + 8}
        dy={8}
        textAnchor="middle"
        fill={chartColors.foreground}
        fontSize={13}
      >
        {`${value} (${(percent * 100).toFixed(1)}%)`}
      </text>
    </g>
  );
};

export default function PieChart({
  data,
  title,
  subtitle,
  height = 300,
  width = "100%",
  className,
  colors = chartColors.series,
  showLegend = true,
  showTooltip = true,
  animate = true,
  legendPosition = "bottom",
  innerRadius = 0,
  outerRadius = "70%",
  activeIndex,
  startAngle = 0,
  endAngle = 360,
  paddingAngle = 0,
}: PieChartProps) {
  const chartData = useMemo(() => {
    return data || generateRandomData(5, 100);
  }, [data]);

  const legendProps = useMemo(() => {
    const baseProps = {
      wrapperStyle: {
        paddingTop: legendPosition === "bottom" ? 20 : 0,
      },
    };

    switch (legendPosition) {
      case "top":
        return { ...baseProps, verticalAlign: "top" as VerticalAlignmentType };
      case "right":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "right" as HorizontalAlignmentType,
        };
      case "left":
        return {
          ...baseProps,
          verticalAlign: "middle" as VerticalAlignmentType,
          align: "left" as HorizontalAlignmentType,
        };
      default:
        return {
          ...baseProps,
          verticalAlign: "bottom" as VerticalAlignmentType,
        };
    }
  }, [legendPosition]);

  const isDoughnut = innerRadius !== 0;

  return (
    <ChartWrapper
      title={title}
      subtitle={subtitle}
      className={className}
      height={height}
      width={width}
    >
      <ResponsiveContainer {...defaultResponsiveConfig}>
        <RechartsPieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
          {showTooltip && <Tooltip />}
          {showLegend && <Legend {...legendProps} />}
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            activeShape={
              activeIndex !== undefined
                ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (props: any) => renderActiveShape(props as ActiveShapeProps)
                : undefined
            }
            activeIndex={activeIndex}
            outerRadius={outerRadius}
            innerRadius={isDoughnut ? innerRadius : 0}
            startAngle={startAngle}
            endAngle={endAngle}
            paddingAngle={paddingAngle}
            fill={colors[0]}
            dataKey="value"
            nameKey="name"
            isAnimationActive={animate}
          >
            {chartData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}
